/**
 * Export Review modal copy — IMPL-002 (Layer 4 acknowledgment).
 *
 * Scope correction (2026-06-12): the requirement is a mandatory
 * ACKNOWLEDGMENT, not an attestation. No affirmation checkboxes, no
 * "I affirm / I take responsibility" framing — legal-weight wording is
 * reserved for patient-authorization flows. The export button itself is
 * the single deliberate acknowledgment action. Brand voice: calm, plain,
 * zero legalese.
 *
 * 🔒 FROZEN COPY (Emily, 2026-06-12): "2026-06-v1" is the frozen, approved
 * wording. These strings are captured VERBATIM by the Smart 40 boundary
 * cycle and may be quoted in the ACL application. ANY wording change from
 * here on MUST bump the version (e.g. "2026-06-v2") — never edit a string
 * under a shipped version ID.
 *
 * The version is sent with every export acknowledgment and recorded in the
 * egress audit entry, pinning exactly which wording the caregiver saw.
 */

/** Mirrors EXPORT_ACK_COPY_VERSION in functions/src/types/generatedDoc.ts. */
export const EXPORT_ACK_COPY_VERSION = '2026-06-v1';

// ── Review notice (both tiers) ──────────────────────────────────────────────

export const DISCLOSURE_COPY = {
  lead: 'Once you export this document, it leaves Hesvara.',
  rest:
    " It includes your care recipient's health information, that's what " +
    "it's for. Give it a final read before sending it to an insurer, " +
    'school, or agency.',
} as const;

/** The single deliberate export action (both tiers). */
export const CONFIRM_BUTTON_LABEL = 'Export';

// ── Elevated tier — letter not backed by citations (tasks/IMPL-002.md §3.1)
// Calm warning notice. The elevated tier is still server-computed and still
// audited as ackType "elevated_null_citations" (ACL weak-data flagging);
// only the UX framing changed. BC-4 widened the trigger to also cover a
// present-but-empty report (totalCount === 0); this frozen 2026-06-v1 copy is
// reused as-is — the body message stays accurate for a zero-citation letter,
// and the audit value string is unchanged.

export const ELEVATED_WARNING_HEADING =
  "This draft doesn't have a citation report.";

export const ELEVATED_WARNING_BODY =
  "Hesvara couldn't check this letter against your documents and care " +
  'logs, so some details may be off. Review it carefully before sending.';

// ── Gate-denial banners (tasks/IMPL-002.md §2.4) ────────────────────────────

export const BANNER_AUDIT_UNAVAILABLE =
  'Could not record this export, so the export was not completed. ' +
  'Please try again. If this persists, contact support.';

export const BANNER_RECONFIRM =
  'The notice below has been updated. Take another look, then export.';

export const BANNER_DOC_GENERATING =
  'This document is still generating. Try again in a moment.';

export const BANNER_DOCUMENT_CHANGED =
  'This document changed after you opened this review, possibly in ' +
  'another tab or by regeneration. Close this dialog and review the ' +
  'latest version before exporting.';

export const BANNER_VERSION_UNAVAILABLE =
  'Could not confirm which version of this document you reviewed. ' +
  'Close this dialog and try again.';

export const BANNER_EXPORT_FAILED =
  'Failed to export document. Please try again.';

export const SUCCESS_BANNER_TEXT = 'Document exported.';
